import { ChartType } from '@ecosystem/newron-client-lib';
import { DataType, Params, Period } from '../data';
import { ReactElement } from 'react';

export enum HTTPMethod {
  GET = 'GET',
  POST = 'POST',
}

export type MarketPeriod = {
  name: string;
  periodicity: string;
  published: string;
  sequence: number;
  value: string;
};

export type Entity = {
  value: string;
  label: string;
  type?: string;
  brandOwner?: string | null;
  parent?: string;
  periods?: MarketPeriod[];
  options?: any;
};

export type GroupedEntity = {
  label: string;
  options: Entity[];
};

export type OpenMarketOptions = {
  value: string;
  periods: MarketPeriod[];
  countryName: string;
  productName: string;
};

export type UpsellProps = {
  openMarketsOptions: OpenMarketOptions[];
  cells: string[];
};

export type DynamicTitle = (string | TitleSelector)[];

export type TitleSelector = {
  menu: SelectionMenu;
};

export type SettingsProvider = {
  values: Params;
  dataSettings: string[];
  enablePageLevelNoDataError?: boolean;
  metadata?: {
    tracking?: {
      elementType?: string;
      elementName?: string;
      chartType?: ChartType;
      updateOnContextChange?: boolean;
      paramsMap?: Record<string, string>;
      rowNameField?: string;
    };
  };
};

export type MenuItemOption = {
  type: 'MenuItem';
  value: string;
  label: string;
  description?: string;
  isDisabled?: boolean;
  isCustom?: boolean;
  isBeta?: boolean;
  isBrand?: boolean;
  brandOwner?: string;
};

export type ListMenuItem = ListBoxMenuItem | MenuItemWithContext | MenuItemDataColumns | GroupedListBoxMenuItem;

export type ListBoxMenuItem = {
  type: 'MenuItem';
  value: string | ListMenuItem[];
  label: string;
  description?: string;
  isDisabled?: boolean;
  isCustom?: boolean;
  isBeta?: boolean;
  isBrand?: boolean;
  indentationDepth?: number;
  brandOwner?: string;
  hasFooter?: boolean;
  footerContent?: ReactElement;
};

export type GroupedListBoxMenuItem = {
  type: 'MenuItemGrouped';
  label: string;
  list: ListMenuItem[];
  hasFooter?: boolean;
  footerContent?: ReactElement;
};

export type MenuItemWithContext = {
  type: 'MenuItemContext';
  contextSetting: string;
  label: string;
  value: string;
  isDisabled?: boolean;
  hasFooter?: boolean;
  footerContent?: ReactElement;
};

export type MenuItemDataColumns = {
  type: 'DataColumns';
  ofType: DataType[];
  requireArrayLength?: boolean;
  hasFooter?: boolean;
  footerContent?: ReactElement;
};

export type MenuItemSeparator = {
  type: 'MenuSeparator';
};

export type MenuItemLabel = {
  type: 'MenuLabel';
  text: string;
};

export type MenuItem =
  | MenuItemOption
  | MenuItemDataColumns
  | MenuItemSeparator
  | MenuItemLabel
  | MenuItemWithContext
  | MenuItemSubMenu;

export type MenuItemSubMenu = {
  type: 'MenuSubMenu';
  label: string;
  items: MenuItem[];
  value?: string;
  disabled?: boolean;
};

export type CardSelectorTrackingMetadata = {
  filterName?: string;
  updateOnContextChange?: boolean;
};

export type SelectionMenu = {
  customMenuOptions?: Period[];
  paramName: string;
  items: ListMenuItem[];
  preventDuplicatesWith?: string[];
  metadata?: { tracking?: CardSelectorTrackingMetadata };
  customizationTraySettings?: CustomizationTraySettings;
  customMenuModalSettings?: CustomMenuModalSettings;
  showGroupLabel?: boolean;
  disableOnSingleItem?: boolean;
};

export type CustomizationTraySettings = {
  trayType: string;
  isCustomizationTrayNeeded: boolean;
};

export type Preference = {
  id: string;
  data: ValueSet;
  created_by?: string;
};

export type ValueSet = Record<string, Params>;

export enum PreferenceName {
  segment_price = 'segment_price',
  segment_feature = 'segment_feature',
}

export type CustomMenuModalSettings = {
  customComparisonPeriods?: CustomComparisonPeriods;
  customTimeSeriesDuration?: CustomTimeSeriesDuration;
  shouldUpdateLabel?: boolean;
};

export type CustomComparisonPeriods = {
  customMenuOptions?: Period[];
  customLimitOption?: string;
};

export type CustomTimeSeriesDuration = {
  customMenuLabels: string;
  maxDuration: number;
  defaultDisplayLabel: string;
};
